import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { TOAST_SETTINGS } from "../../utils/siteSettings";
import { submitReferral } from "./submitReferral";

const popupStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  background: "rgba(0, 0, 0, 0.7)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 999,
  padding: 16,
};

const contentStyle = {
  position: "relative",
  background: "#fff",
  borderRadius: 8,
  width: "100%",
  maxWidth: 520,
  padding: 28,
  boxShadow: "0 10px 24px rgba(0, 0, 0, 0.25)",
  maxHeight: "calc(100vh - 32px)",
  overflowY: "auto",
};

const fieldRow = { marginBottom: 14 };
const labelStyle = {
  display: "block",
  fontSize: 13,
  fontWeight: 600,
  color: "#1f2a14",
  marginBottom: 4,
};
const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  fontSize: 14,
  border: "1px solid #d8dec6",
  borderRadius: 4,
  background: "#fff",
  color: "#1f2a14",
  boxSizing: "border-box",
};
const inputErrorStyle = { ...inputStyle, borderColor: "#b00020" };
const fieldErrorStyle = {
  color: "#b00020",
  fontSize: 12,
  marginTop: 4,
};
const banner = {
  color: "#b00020",
  background: "#fdecea",
  border: "1px solid #f5c2c0",
  padding: "10px 12px",
  borderRadius: 4,
  marginBottom: 16,
  fontSize: 14,
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ReferralFormPopup = ({ onClose }) => {
  const dispatch = useDispatch();
  const member = useSelector(
    (state) => state?.fetchSiteSettings?.site_settings?.member
  );

  const [form, setForm] = useState({
    their_company_name: member?.mem_business || "",
    referral_company_name: "",
    referral_contact_name: "",
    referral_phone: "",
    referral_email: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [banner_msg, setBanner] = useState("");

  const setField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    if (fieldErrors[key]) {
      setFieldErrors((prev) => ({ ...prev, [key]: "" }));
    }
    if (banner_msg) setBanner("");
  };

  const validate = () => {
    const errs = {};
    if (!form.their_company_name.trim()) {
      errs.their_company_name = "Your company name is required.";
    }
    if (!form.referral_company_name.trim()) {
      errs.referral_company_name = "Referral company is required.";
    }
    if (!form.referral_contact_name.trim()) {
      errs.referral_contact_name = "Contact name is required.";
    }
    if (!form.referral_phone.trim()) {
      errs.referral_phone = "Phone number is required.";
    }
    const email = form.referral_email.trim();
    if (!email) {
      errs.referral_email = "Email is required.";
    } else if (!EMAIL_RE.test(email)) {
      errs.referral_email = "Enter a valid email address.";
    } else if (
      member?.mem_email &&
      email.toLowerCase() === member.mem_email.toLowerCase()
    ) {
      // Client-side self-referral hint; backend re-validates authoritatively.
      errs.referral_email = "You can't refer your own account.";
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate() || submitting) return;
    setSubmitting(true);
    setBanner("");
    try {
      const data = await dispatch(
        submitReferral({
          their_company_name: form.their_company_name.trim(),
          referral_company_name: form.referral_company_name.trim(),
          referral_contact_name: form.referral_contact_name.trim(),
          referral_phone: form.referral_phone.trim(),
          referral_email: form.referral_email.trim(),
        })
      );
      if (data?.status === 1) {
        toast.success(
          data?.msg || "Thanks! We'll be in touch with your referral.",
          TOAST_SETTINGS
        );
        onClose();
      } else {
        // Map common backend rejection codes onto inline field errors when
        // we can; otherwise show the message in the banner.
        const code = data?.code;
        const msg = data?.msg || "Unable to submit the referral.";
        if (code === "self_referral") {
          setFieldErrors((prev) => ({ ...prev, referral_email: msg }));
        } else if (code === "existing_customer" || code === "duplicate") {
          setFieldErrors((prev) => ({ ...prev, referral_email: msg }));
        } else {
          setBanner(msg);
        }
      }
    } catch (err) {
      setBanner(
        err?.response?.data?.msg ||
          err?.message ||
          "Network error — please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={popupStyle}>
      <div style={contentStyle}>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            background: "transparent",
            border: "none",
            fontSize: 22,
            cursor: "pointer",
            color: "#666",
            lineHeight: 1,
            padding: 4,
          }}
        >
          ×
        </button>

        <h5
          style={{
            margin: "0 0 6px",
            fontSize: 20,
            fontWeight: 700,
            color: "#1f2a14",
          }}
        >
          Refer a contractor — earn $200
        </h5>
        <p
          style={{
            marginTop: 0,
            marginBottom: 20,
            fontSize: 13,
            color: "#5a6047",
            lineHeight: 1.5,
          }}
        >
          Tell us who to reach out to. You earn $200 after they complete
          their first paid month.
        </p>

        {banner_msg && <div style={banner}>{banner_msg}</div>}

        <form onSubmit={handleSubmit} noValidate>
          <div style={fieldRow}>
            <label htmlFor="ref-their-company" style={labelStyle}>
              Your company name
            </label>
            <input
              id="ref-their-company"
              type="text"
              value={form.their_company_name}
              onChange={setField("their_company_name")}
              style={
                fieldErrors.their_company_name ? inputErrorStyle : inputStyle
              }
              autoComplete="organization"
            />
            {fieldErrors.their_company_name && (
              <div style={fieldErrorStyle}>
                {fieldErrors.their_company_name}
              </div>
            )}
          </div>

          <div style={fieldRow}>
            <label htmlFor="ref-company" style={labelStyle}>
              Referral company name
            </label>
            <input
              id="ref-company"
              type="text"
              value={form.referral_company_name}
              onChange={setField("referral_company_name")}
              style={
                fieldErrors.referral_company_name
                  ? inputErrorStyle
                  : inputStyle
              }
            />
            {fieldErrors.referral_company_name && (
              <div style={fieldErrorStyle}>
                {fieldErrors.referral_company_name}
              </div>
            )}
          </div>

          <div style={fieldRow}>
            <label htmlFor="ref-name" style={labelStyle}>
              Referral contact name
            </label>
            <input
              id="ref-name"
              type="text"
              value={form.referral_contact_name}
              onChange={setField("referral_contact_name")}
              style={
                fieldErrors.referral_contact_name
                  ? inputErrorStyle
                  : inputStyle
              }
              autoComplete="name"
            />
            {fieldErrors.referral_contact_name && (
              <div style={fieldErrorStyle}>
                {fieldErrors.referral_contact_name}
              </div>
            )}
          </div>

          <div style={fieldRow}>
            <label htmlFor="ref-phone" style={labelStyle}>
              Referral phone
            </label>
            <input
              id="ref-phone"
              type="tel"
              value={form.referral_phone}
              onChange={setField("referral_phone")}
              style={fieldErrors.referral_phone ? inputErrorStyle : inputStyle}
              autoComplete="tel"
            />
            {fieldErrors.referral_phone && (
              <div style={fieldErrorStyle}>{fieldErrors.referral_phone}</div>
            )}
          </div>

          <div style={fieldRow}>
            <label htmlFor="ref-email" style={labelStyle}>
              Referral email
            </label>
            <input
              id="ref-email"
              type="email"
              value={form.referral_email}
              onChange={setField("referral_email")}
              style={fieldErrors.referral_email ? inputErrorStyle : inputStyle}
              autoComplete="email"
            />
            {fieldErrors.referral_email && (
              <div style={fieldErrorStyle}>{fieldErrors.referral_email}</div>
            )}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              marginTop: 22,
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="site_btn blank"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="site_btn"
            >
              {submitting ? "Submitting…" : "Submit Referral"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReferralFormPopup;
